import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Save } from "lucide-react";
import { toast } from "sonner";
import { reApi, errMsg } from "@/lib/residentApi";

const Stat = ({ label, value, testId, tone = "text-[#d4a017]" }) => (
  <div className="p-4 border border-white/10 rounded-sm bg-white/[0.02]">
    <div className="font-mono text-[9px] tracking-[0.22em] uppercase text-white/50">{label}</div>
    <div data-testid={testId} className={`mt-1 text-2xl font-semibold ${tone}`}>{value}</div>
  </div>
);

function BudgetPanel() {
  const [b, setB] = useState(null);
  const [cap, setCap] = useState("");
  const load = () => reApi.get("/llm-budget").then((d) => { setB(d); setCap(String(d.monthly_fresh_cap)); }).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  const save = async () => {
    try { await reApi.patch("/llm-budget", { monthly_fresh_cap: Number(cap) }); toast.success("Cap updated"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  if (!b) return null;
  return (
    <section data-testid="llm-budget-panel" className="space-y-3">
      <h2 className="text-base md:text-lg font-semibold">AI question budget · {b.current.month}</h2>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Fresh Gemini calls" value={b.current.fresh_calls || 0} testId="llm-fresh" />
        <Stat label="Cache hits" value={b.current.cached_hits || 0} testId="llm-cached" />
        <Stat label="Total requests" value={b.current.total_requests || 0} testId="llm-total" />
        <Stat label="Remaining this month" value={b.remaining} testId="llm-remaining" tone="text-emerald-300" />
      </div>
      <div className="flex items-center gap-2">
        <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/50">Monthly fresh-call cap</label>
        <input data-testid="llm-cap-input" type="number" min={0} value={cap} onChange={(e) => setCap(e.target.value)}
          className="w-28 px-2 py-1.5 bg-black/40 border border-white/10 focus:border-[#d4a017] outline-none text-sm rounded-sm" />
        <button type="button" data-testid="llm-cap-save" onClick={save}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#d4a017] text-[#d4a017] font-mono text-[10px] tracking-[0.2em] uppercase rounded-sm hover:bg-[#d4a017]/10">
          <Save className="h-3 w-3" /> Save
        </button>
      </div>
    </section>
  );
}

export default function PillarHealthPage() {
  const [h, setH] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => { reApi.get("/pillar-health").then(setH).catch((e) => setError(errMsg(e))); }, []);
  return (
    <main data-testid="pillar-health-page" className="min-h-screen bg-[#0b0814] text-white px-6 py-10">
      <div className="max-w-5xl mx-auto space-y-8">
        <Link to="/admin" data-testid="pillar-health-back" className="inline-flex items-center gap-1 font-mono text-[10px] tracking-[0.22em] uppercase text-[#d4a017]"><ArrowLeft className="h-3.5 w-3.5" /> Admin OS</Link>
        <div>
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#d4a017]">System · Super Admin</div>
          <h1 className="text-4xl sm:text-5xl font-semibold mt-1">Pillar Health</h1>
        </div>
        {error ? <p data-testid="pillar-health-error" className="text-red-300">{error}</p> : h && (
          <>
            <div className="grid grid-cols-3 gap-3">
              <Stat label="Residents tracked" value={h.residents_tracked} testId="ph-tracked" />
              <Stat label="Avg completion" value={`${h.avg_completion_pct}%`} testId="ph-avg" />
              <Stat label="Behind pace" value={h.behind} testId="ph-behind" tone="text-red-300" />
            </div>
            <div className="overflow-x-auto border border-white/10 rounded-sm">
              <table data-testid="pillar-health-table" className="w-full text-sm">
                <thead className="font-mono text-[9px] tracking-[0.2em] uppercase text-white/50 bg-white/[0.03]">
                  <tr>{["Pillar", "Pending", "Awaiting staff", "Assessed", "Overdue", "Urgent"].map((c) => <th key={c} className="text-left px-4 py-2">{c}</th>)}</tr>
                </thead>
                <tbody>
                  {h.pillars.map((p) => (
                    <tr key={p.id} data-testid={`ph-row-${p.id}`} className="border-t border-white/5">
                      <td className="px-4 py-2.5 flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ background: p.color_hex === "#0A0612" ? "#9b8cff" : p.color_hex }} />{p.name}</td>
                      <td className="px-4 py-2.5">{p.pending}</td>
                      <td className="px-4 py-2.5 text-[#d4a017]">{p.resident_submitted}</td>
                      <td className="px-4 py-2.5 text-emerald-300">{p.staff_assessed}</td>
                      <td className="px-4 py-2.5 text-red-300">{p.overdue}</td>
                      <td className="px-4 py-2.5 text-red-300">{p.urgent}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        <BudgetPanel />
      </div>
    </main>
  );
}
